/**
 * JSON-LD script wrapper for the halcy.ai landing site.
 *
 * Single source of truth for rendering JSON-LD <script> tags within apps/landing.
 * Uses compact JSON serialization (no indentation) to minimize page weight.
 *
 * AI-NOTE: escaping lives in `serializeJsonForScript` from
 * `@marketing-optimizer/ontology`, shared with
 * packages/generators/src/components/json-ld/JsonLdScript.tsx. That copy
 * differs only in pretty-printing.
 */

import React, { type JSX } from 'react';
import { serializeJsonForScript } from '@marketing-optimizer/ontology';

interface JsonLdScriptProps {
  /** The JSON-LD schema object to serialize and render. */
  schema: Record<string, unknown>;
}

/**
 * Renders a JSON-LD schema as a `<script type="application/ld+json">` tag.
 *
 * @example
 * ```tsx
 * return <JsonLdScript schema={{ '@context': 'https://schema.org', '@type': 'WebSite' }} />;
 * ```
 */
export function JsonLdScript({ schema }: JsonLdScriptProps): JSX.Element {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serializeJsonForScript(schema) }}
    />
  );
}
